import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { OntologyEntity, OntologyEntityType } from "./types";
import { compactIri, entityDisplayName } from "./types";
import Chip from "../../../ui/feedback/Chip";
import { chipClassName } from "../../../ui/styles/chipStyles";
import { classNames } from "../../../ui/styles/classNames";

/** How the detail panes name and open the terms they mention. */
export interface EntityLookup {
  readonly entitiesByIri: ReadonlyMap<string, OntologyEntity>;
  readonly entityTypeByIri: ReadonlyMap<string, OntologyEntityType>;
  readonly onSelectRelatedEntity: (iri: string, entityType: OntologyEntityType) => void;
}

/** A term's name and IRI, with an optional badge beside the name and lines below. */
export function EntityHeader({ name, iri, badge, children }: { name: string; iri: string; badge?: ReactNode; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <header className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="app-title break-words text-xl font-semibold">{name}</h3>
        {badge}
      </div>
      <details className="app-text-muted text-xs">
        <summary className="cursor-pointer">{t("ontologyViewer.details")}</summary>
        <p className="break-all font-mono">{iri}</p>
      </details>
      {children}
    </header>
  );
}

export function DetailSection({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="app-divider-top pt-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h4 className="app-title text-sm font-semibold">{title}</h4>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** A term by its name: a button that opens it if the version declares it, else plain text. */
export function EntityChip({ iri, lookup }: { iri: string; lookup: EntityLookup }) {
  const { t } = useTranslation();
  const related = lookup.entitiesByIri.get(iri);
  const relatedType = lookup.entityTypeByIri.get(iri);
  const label = related ? entityDisplayName(related) : compactIri(iri);
  if (related && relatedType) {
    return (
      <button
        type="button"
        className={classNames(chipClassName(), "app-action-link max-w-full cursor-pointer text-left hover:underline")}
        title={iri}
        aria-label={t("ontologyViewer.openEntity", { name: label })}
        onClick={() => {
          lookup.onSelectRelatedEntity(iri, relatedType);
        }}
      >
        {label}
      </button>
    );
  }
  return (
    <Chip title={iri} className="max-w-full">
      {label}
    </Chip>
  );
}

export function EntityChipList({ values, lookup, emptyText }: { values: readonly string[]; lookup: EntityLookup; emptyText: string }) {
  if (values.length === 0) {
    return <p className="app-text-muted text-sm">{emptyText}</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {values.map((iri) => (
        <EntityChip key={iri} iri={iri} lookup={lookup} />
      ))}
    </div>
  );
}
